import type React from "react";
import { useTranslation } from "react-i18next";

export type StatusBadgeKind =
  | "order"
  | "orderType"
  | "transaction"
  | "refund"
  | "refundType"
  | "staff"
  | "generic";

export const STATUS_BADGE_CLASS: Record<string, string> = {
  new: "bg-rose-50 text-rose-700 ring-rose-200",
  pending: "bg-amber-50 text-amber-700 ring-amber-200",
  pending_review: "bg-yellow-50 text-yellow-700 ring-yellow-200",
  confirmed: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  preparing: "bg-orange-50 text-orange-700 ring-orange-200",
  ready: "bg-lime-50 text-lime-700 ring-lime-200",
  out_for_delivery: "bg-sky-50 text-sky-700 ring-sky-200",
  in_transit: "bg-blue-50 text-blue-700 ring-blue-200",
  delivered: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  completed: "bg-green-50 text-green-700 ring-green-200",
  cancelled: "bg-red-50 text-red-700 ring-red-200",

  pickup: "bg-teal-50 text-teal-700 ring-teal-200",
  delivery: "bg-cyan-50 text-cyan-700 ring-cyan-200",

  success: "bg-green-100 text-green-800 ring-green-300",
  partial_refund: "bg-orange-100 text-orange-800 ring-orange-300",
  partially_refunded: "bg-amber-100 text-amber-800 ring-amber-300",
  refunded: "bg-purple-50 text-purple-700 ring-purple-200",
  failed: "bg-rose-100 text-rose-800 ring-rose-300",
  rejected: "bg-red-100 text-red-800 ring-red-300",
  approved: "bg-emerald-100 text-emerald-800 ring-emerald-300",

  full: "bg-teal-100 text-teal-800 ring-teal-300",
  partial: "bg-yellow-100 text-yellow-800 ring-yellow-300",

  online: "bg-lime-100 text-lime-800 ring-lime-300",
  active: "bg-cyan-100 text-cyan-800 ring-cyan-300",
  offline: "bg-slate-50 text-slate-700 ring-slate-200",
  inactive: "bg-gray-50 text-gray-700 ring-gray-200",
  paused: "bg-zinc-50 text-zinc-700 ring-zinc-200",
};

const FALLBACK_BADGE_CLASS = "bg-[#f1f5f9] text-[#475569] ring-[#e2e8f0]";

const LABEL_KEY_BY_KIND: Record<StatusBadgeKind, Record<string, string>> = {
  order: {
    new: "orders.status.new",
    pending: "orders.status.pending",
    preparing: "orders.status.preparing",
    ready: "orders.status.ready",
    out_for_delivery: "orders.status.out_for_delivery",
    in_transit: "orders.status.in_transit",
    delivered: "orders.status.delivered",
    completed: "orders.status.completed",
    cancelled: "orders.status.cancelled",
  },
  orderType: {
    pickup: "orders.type.pickup",
    delivery: "orders.type.delivery",
  },
  transaction: {
    success: "transactions.statusSuccess",
    partial_refund: "transactions.statusPartialRefund",
    refunded: "transactions.statusRefunded",
    pending: "transactions.statusPending",
    failed: "transactions.statusFailed",
  },
  refund: {
    success: "refunds.pillSuccess",
    completed: "refunds.pillSuccess",
    approved: "refunds.pillSuccess",
    pending: "refunds.pillPending",
    rejected: "refunds.pillRejected",
    failed: "refunds.pillFailed",
  },
  refundType: {
    full: "refunds.typeFullRef",
    partial: "refunds.typePartialRef",
  },
  staff: {
    online: "staff.online",
    active: "staff.active",
    inactive: "staff.inactive",
  },
  generic: {},
};

export function getStatusBadgeClass(status: string | null | undefined) {
  const key = normalizeStatusBadgeKey(status);
  return STATUS_BADGE_CLASS[key] ?? FALLBACK_BADGE_CLASS;
}

export function normalizeStatusBadgeKey(status: string | null | undefined) {
  return String(status ?? "")
    .trim()
    .replace(/-/g, "_")
    .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    .replace(/^_/, "");
}

export function StatusBadge({
  status,
  kind = "generic",
  label,
  className = "",
  size = "sm",
  icon,
}: {
  status: string | null | undefined;
  kind?: StatusBadgeKind;
  label?: string;
  className?: string;
  size?: "xs" | "sm" | "md";
  icon?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const key = normalizeStatusBadgeKey(status);
  const labelKey = LABEL_KEY_BY_KIND[kind]?.[key];
  const text = label ?? (labelKey ? t(labelKey) : key.replace(/_/g, " "));
  const sizeClass = size === "xs"
    ? "px-2.5 py-1 text-[10px]"
    : size === "md"
    ? "px-4 py-2 text-xs"
    : "px-3 py-1 text-[11px]";

  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-black uppercase tracking-[0.5px] ring-1 ring-inset ${sizeClass} ${getStatusBadgeClass(key)} ${className}`}>
      {icon}
      {text}
    </span>
  );
}
